import type { LocationDescriptorObject } from 'history';
import { List } from 'immutable';
import paths from '~/app/paths/paths';
import MachineFilterV2Constants from '~/ducks/machine-filter-v2/constants';
import { FilterGroupRecord, NameRecord } from '~/ducks/machine-filter-v2/data';
import type { FilterDefinitionType } from '~/ducks/machine-filter-v2/data/definitions';
import {
  FreeTextFilterRecord,
  RangeFilterRecord,
  SelectFilterRecord,
  TagFilterRecord,
} from '~/ducks/machine-filter-v2/data/filter-values';
import type { RangeType } from '~/ducks/machine-filter-v2/data/filter-values/RangeFilterRecord';
import FilterDefinitionValueRecord from '~/ducks/machine-filter-v2/data/FilterDefinitionValueRecord';
import type FilterSectionRecord from '~/ducks/machine-filter-v2/data/FilterSectionRecord';
import type SavedFilterRecord from '~/ducks/machine-filter-v2/data/SavedFilterRecord';
import type { MappedFilter } from '~/ducks/machine-filter-v2/types/api-post-types';
import type {
  FilterTypes,
  SavableFilterTypes,
} from '~/ducks/machine-filter-v2/types/filter-value-types';
import Logger from '~/utils/logger/logger';
import { getValueFromQueryString } from '~/utils/query-helper/query-helper';

type FilterMap = Record<string, string | string[]>;

export const getFilterMapFromFilters = (activeFilters: List<FilterTypes>): FilterMap =>
  activeFilters.reduce((acc, filterRecord) => {
    const filterKey = filterRecord.getFilterKey();

    if (filterRecord instanceof TagFilterRecord || filterRecord instanceof SelectFilterRecord) {
      acc[`filter[${filterKey}]`] = acc[`filter[${filterKey}]`] || [];
      acc[`filter[${filterKey}]`].push(
        filterRecord
          .getValues()
          .map((val) => val.getValue())
          .join(',')
      );
    } else if (filterRecord instanceof FreeTextFilterRecord) {
      acc[`filter[${filterKey}]`] = encodeURIComponent(filterRecord.getValue());
    } else if (filterRecord instanceof RangeFilterRecord) {
      acc[`filter[${filterKey}]`] = filterRecord.getValues().join(',');
    }

    return acc;
  }, {});

export const getFilterObject = (activeFilters: List<FilterTypes>): Record<string, unknown> => {
  if (activeFilters.size === 0) {
    return {};
  }

  return activeFilters.reduce<Record<string, string | string[] | RangeType[]>>(
    (acc, filterValue) => {
      const filterKey = filterValue.getFilterKey();
      if (!filterKey) {
        return acc;
      }

      if (filterValue instanceof TagFilterRecord || filterValue instanceof SelectFilterRecord) {
        acc[filterKey] = acc[filterKey] || [];
        acc[filterKey] = (acc[filterKey] as string[]).concat(
          filterValue
            .getValues()
            .map((value) => value.getValue())
            .toJS()
        );
      } else if (filterValue instanceof RangeFilterRecord) {
        acc[filterKey] = acc[filterKey] || [];
        acc[filterKey] = (acc[filterKey] as RangeType[]).concat(filterValue.getValues());
      } else if (filterValue instanceof FreeTextFilterRecord) {
        acc[filterKey] = filterValue.getValue();
      }

      return acc;
    },
    {}
  );
};

export const getSearchStringFromFilters = (activeFilters: List<FilterTypes>): string => {
  if (activeFilters.size === 0) {
    return '';
  }

  const searchStrings = getFilterObject(activeFilters);
  return `filters=${JSON.stringify(searchStrings)}`;
};

const getUniqueTagCategories = (values: string[]): string[] => {
  return [...new Set(values.map((value) => value.split(':')[0]))] as string[];
};

const createTagFilterRecords = (tagCategories: string[], values: string[]): TagFilterRecord[] => {
  return tagCategories.map((tagCategory) => {
    const valuesWithThisTagCategory: string[] = values.filter((f) => f.startsWith(tagCategory));

    return new TagFilterRecord({
      filterName: NameRecord.createTagsNameRecord(tagCategory),
      // @ts-ignore
      selectedOptions: valuesWithThisTagCategory.map((tagValue) => ({
        value: tagValue,
        label: tagValue.replace(`${tagCategory}:`, ''),
      })),
    });
  });
};

type ParsedFilters = Record<string, string | string[] | RangeType>;

export const parseFiltersFromSearchString = (searchString: string): List<SavableFilterTypes> => {
  if (!searchString) {
    return List();
  }

  const filtersString = getValueFromQueryString(searchString, 'filters');
  if (!filtersString) {
    return List();
  }

  try {
    const parsedFilters: ParsedFilters = JSON.parse(filtersString);
    const filterKeys = Object.keys(parsedFilters);

    const filterRecords = filterKeys.reduce<SavableFilterTypes[]>((acc, key: string) => {
      const values = parsedFilters[key];
      const nameRecord = new NameRecord({ name: key });

      if (key === MachineFilterV2Constants.TAGS_FILTER_NAME) {
        const tagCategories = getUniqueTagCategories(values as string[]);
        const tagFilterRecords = createTagFilterRecords(tagCategories, values as string[]);

        acc = acc.concat(tagFilterRecords);
      } else if (Array.isArray(values)) {
        if (
          values.length === 2 &&
          (values as Array<unknown>).every((val) => typeof val === 'number')
        ) {
          acc.push(new RangeFilterRecord({ filterName: nameRecord, values }));
        } else {
          const valuesList = List<FilterDefinitionValueRecord>(
            (values as string[]).map(
              (value) =>
                new FilterDefinitionValueRecord({
                  value,
                })
            )
          );
          acc.push(
            new SelectFilterRecord({
              filterName: nameRecord,
              selectedOptions: valuesList,
            })
          );
        }
      } else if (key === MachineFilterV2Constants.FREE_TEXT_FILTER_NAME) {
        acc.push(
          new FreeTextFilterRecord({
            filterName: nameRecord.set('translationKey', 'web.asset_filter.search.placeholder'),
            value: values,
          })
        );
      } else {
        acc.push(new FreeTextFilterRecord({ filterName: nameRecord, value: values }));
      }
      return acc;
    }, []);

    return List(filterRecords);
  } catch (err) {
    Logger.error('Could not parse filters from location', searchString, 'Caught error:', err);
    return List();
  }
};

export const flattenSections = (
  filterSections: List<FilterSectionRecord>
): List<FilterDefinitionType> =>
  List(filterSections.reduce((acc, section) => acc.merge(section.getFilterDefinitions()), List()));

type FilterDefinitionMap = Record<string, FilterDefinitionType>;

const filterDefinitionListToMap = (
  filterDefinitions: List<FilterDefinitionType>
): FilterDefinitionMap => {
  return filterDefinitions.reduce<FilterDefinitionMap>((acc, filterDefinition) => {
    acc[filterDefinition.getName()] = filterDefinition;
    return acc;
  }, {});
};

export const convertTemporaryFiltersToDefinitionFilters = (
  filterDefinitions: List<FilterDefinitionType>,
  temporaryFilters: List<SavableFilterTypes>
): List<SavableFilterTypes> => {
  const definitionMap = filterDefinitionListToMap(filterDefinitions);

  return temporaryFilters.map((tempFilter) => {
    if (tempFilter.getName() in definitionMap) {
      return tempFilter.updateFromDefinition(definitionMap[tempFilter.getName()]);
    }
    return tempFilter;
  });
};

export const mapFilterValuesToSaveFilterStructure = (
  filterValues: List<SavableFilterTypes>
): MappedFilter[] => {
  return filterValues.reduce<MappedFilter[]>((acc, curr) => {
    const newRow: MappedFilter = {
      name: curr.getFilterKey(),
      type: curr.getType(),
    };

    if (curr instanceof SelectFilterRecord || curr instanceof TagFilterRecord) {
      newRow.selectedOptions = curr
        .getValues()
        .map((filterValue) => filterValue.getValue())
        .toJS();
    } else if (curr instanceof RangeFilterRecord) {
      newRow.values = curr.getValues();
    } else if (curr instanceof FreeTextFilterRecord) {
      newRow.value = curr.getValues();
    }

    acc.push(newRow);
    return acc;
  }, []);
};

const getFilterIndex = (filters, filterName): number =>
  filters.findIndex((filter) => filter.getName() === filterName);

export const removeFilterValue = <T extends SavedFilterRecord | FilterGroupRecord>(
  stateRecord: T,
  filterName: string,
  filterValue?: FilterDefinitionValueRecord | 'min' | 'max' | string
): T => {
  const presentFilterIndex = getFilterIndex(stateRecord.filters, filterName);

  if (presentFilterIndex >= 0) {
    const updated = stateRecord.updateIn(['filters', presentFilterIndex], (filter: FilterTypes) =>
      filter.removeValue(filterValue as any)
    );

    // TODO Figure out correct typings for this...
    // @ts-ignore
    return updated.update('filters', (filters) => filters.filter((f) => !!f));
  }

  Logger.error('Filter not found when trying to remove it from FilterGroupRecord');
  return stateRecord;
};

export const getAssetListLink = (
  filters: List<FilterTypes>,
  assetId?: AssetId
): Pick<LocationDescriptorObject, 'search' | 'pathname'> => {
  const search = getSearchStringFromFilters(filters);

  if (assetId) {
    return {
      search,
      pathname: paths.getMachinesOrInventory().getPath({ machineId: assetId }),
    };
  }

  return {
    search,
    pathname: paths.getMachinesOrInventory().getPath(),
  };
};

export const generateSearchString = (
  newSearch: Record<string, string | Record<string, string | string[]>> = {},
  oldSearch: Record<string, string | Record<string, string | string[]>> = {}
): string => {
  if (typeof newSearch !== 'object') {
    return '';
  }

  const searchStrings: string[] = [];
  Object.entries(newSearch).forEach(([key, values]) => {
    if (typeof values === 'string') {
      searchStrings.push(`${key}=${values}`);
    } else {
      const newValues = {
        ...(oldSearch[key] as Record<string, string>),
        ...values,
      };
      searchStrings.push(`${key}=${JSON.stringify(newValues)}`);
    }
  });

  return searchStrings.length > 0 ? searchStrings.join('&') : '';
};
